import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./completedTransfers.module.scss";
import { COMPLETED_TRANSFERS_TAB, HEADER, TABLE_HEADERS } from "./constants";
import SearchInput from "../../components/common/searchInput";
import { Tab, Tabs } from "@mui/material";
import CompletedTransfersTableShimmer from "./CompletedTransfersTableShimmer";
import CompletedTransfersListItem from "./CompletedTransfersListItem";
import Paginations from "../../components/common/Pagination";
import { getCompletedTransfers } from "../../setup/api/services/completed-transfers";
import { ICompletedTransferBody } from "../../setup/api/services/completed-transfers/models";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;

const CompletedTransfers = () => {
  const userIdTimeoutRef = useRef<NodeJS.Timer>();
  const transactionIdTimeoutRef = useRef<NodeJS.Timer>();
  const [completedTransfers, setCompletedTransfers] = useState<
    ICompletedTransferBody[]
  >([]);
  const [userIdSearchValue, setUserIdSearchValue] = useState("");
  const [transactionIdSearchValue, setTransactionIdSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const fetchCompletedTransfers = useCallback(
    async (beneficiaryId?: string, transactionId?: string) => {
      setLoading(true);
      try {
        const body = {
          beneficiaryId,
          transactionId,
          page: page - 1,
          size: PAGE_SIZE,
        };
        const response = await getCompletedTransfers(body);
        setCompletedTransfers(response.data.content);
        setPagesCount(response.data.totalPages);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setCompletedTransfers([]);
      }
    },
    [page]
  );

  const handleUserIdSearchValue = useCallback(
    (event) => {
      clearTimeout(userIdTimeoutRef.current);
      const value = event.target.value;
      setUserIdSearchValue(value);
      // Clear transaction ID when searching by user ID
      setTransactionIdSearchValue("");
      userIdTimeoutRef.current = setTimeout(() => {
        fetchCompletedTransfers(value, "");
      }, 300);
    },
    [fetchCompletedTransfers]
  );

  const handleTransactionIdSearchValue = useCallback(
    (event) => {
      clearTimeout(transactionIdTimeoutRef.current);
      const value = event.target.value;
      setTransactionIdSearchValue(value);
      // Clear user ID when searching by transaction ID
      setUserIdSearchValue("");
      transactionIdTimeoutRef.current = setTimeout(() => {
        fetchCompletedTransfers("", value);
      }, 300);
    },
    [fetchCompletedTransfers]
  );

  useEffect(() => {
    fetchCompletedTransfers(userIdSearchValue, transactionIdSearchValue);
  }, [
    page,
    fetchCompletedTransfers,
    userIdSearchValue,
    transactionIdSearchValue,
  ]);

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  return (
    <div className={styles["page_layout"]}>
      <h1>{HEADER}</h1>
      <div className={styles.header_container}>
        <div className={styles["right_section"]}>
          <Tabs
            style={{ alignItems: "center" }}
            className={styles.tab_wrapper}
            value={"completed"}>
            <Tab
              value="completed"
              label={COMPLETED_TRANSFERS_TAB}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
          </Tabs>
        </div>
        <div className={styles["left_section"]}>
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_user_id")}
            value={userIdSearchValue}
            style={{ height: "35px", width: "252px", marginLeft: "10px" }}
            handleChange={handleUserIdSearchValue}
          />
          <SearchInput
            dir="ltr"
            placeholder={t("lbl_search_by_transaction_id")}
            value={transactionIdSearchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={handleTransactionIdSearchValue}
          />
        </div>
      </div>

      <table className={styles.table}>
        <tr className={styles.table_header}>
          <th className={styles.hint}>{TABLE_HEADERS.USERNAME}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSACTION_ID}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_TIME}</th>
          <th className={styles.hint}>{TABLE_HEADERS.AMOUNT}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_CHANNEL}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSFER_TYPE}</th>
        </tr>
        {loading ? (
          <CompletedTransfersTableShimmer />
        ) : (
          <tbody className={styles.table_body}>
            {completedTransfers?.map((transfer, index) => (
              <CompletedTransfersListItem key={index} transfer={transfer} />
            ))}
          </tbody>
        )}
      </table>

      <>
        <div
          className={
            completedTransfers?.length ? "paginatorContainer" : "hide"
          }>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};

export default CompletedTransfers;
